import type { NavItem, NavSection } from '@/constants/app/navigation.constant.js'
import type { PermissionName } from '@/constants/app/permission.constant.js'

/**
 * Which sections and items this operator may see.
 *
 * In `utils/` rather than inside `AppLayout.vue` because **this is the hard part**, and
 * because the router needs it too (the landing route goes to the first visible screen):
 * it decides what an operator is told exists. A component test would have to mount a shell,
 * a router and a store to ask it one question; here it is a pure function over two
 * arguments and the answers are asserted directly.
 */
export function visibleItems(
  items: readonly NavItem[],
  canAll: (required: readonly PermissionName[]) => boolean,
): readonly NavItem[] {
  // An item with no `requires` is visible to any signed-in operator — "my account" is
  // theirs, and the backend's route agrees by carrying no permission.
  return items.filter((item) => item.requires === undefined || canAll(item.requires))
}

/**
 * A section with nothing visible in it is removed entirely.
 *
 * Leaving an empty section would advertise a capability the operator cannot reach and
 * cannot be granted by anything they can see — the rail would show "Revenue", clicking it
 * would show nothing, and the only reading available is "the panel is broken".
 */
export function visibleSections(
  sections: readonly NavSection[],
  canAll: (required: readonly PermissionName[]) => boolean,
): readonly NavSection[] {
  return sections
    .map((section) => ({ ...section, items: visibleItems(section.items, canAll) }))
    .filter((section) => section.items.length > 0)
}

/**
 * Which rail section is active.
 *
 * **A section the operator CLICKED wins over the one that owns the current route**, until
 * the next navigation clears the click. The first version had this inverted — `owning ??
 * chosen` — so on `/roles` the Admin section always answered first and clicking Merchants
 * in the rail visibly did nothing. That is the "sidebar not navigating" bug.
 *
 * `routeName` is the current route or, for a detail route, its `meta.parent` list route.
 */
export function activeSectionId(
  sections: readonly NavSection[],
  routeName: string | null,
  chosen: string | null,
): string | null {
  if (chosen !== null && sections.some((section) => section.id === chosen)) return chosen

  const owning = sections.find((section) =>
    section.items.some((item) => item.route !== null && item.route === routeName),
  )

  return owning?.id ?? sections[0]?.id ?? null
}

/** A navigable target: route name plus the item's query, if it carries one. */
export interface NavTarget {
  readonly name: string
  readonly query?: Readonly<Record<string, string>>
}

/** The screen a rail click should land on: the section's first built item, if any. */
export function firstRouteIn(section: NavSection): NavTarget | null {
  const item = section.items.find((candidate) => candidate.available && candidate.route !== null)

  if (item === undefined || item.route === null) return null

  return { name: item.route, ...(item.query !== undefined ? { query: item.query } : {}) }
}

/**
 * Whether a nav item is the one the operator is ON.
 *
 * Route name must match, and every key the item's query names must match the URL. Two
 * items that share a route ("Shop promotions" / "Default promotions") therefore highlight
 * separately, and a plain `/promotions` with no scope highlights neither — correct, since
 * neither item describes it.
 */
export function isItemActive(item: NavItem, routeName: string | null, query: Readonly<Record<string, unknown>>): boolean {
  if (item.route === null || item.route !== routeName) return false

  return Object.entries(item.query ?? {}).every(([key, value]) => query[key] === value)
}
